import React, { useMemo } from 'react';
import type {
  GeometryEdge,
  GeometryLabels,
  GeometryAnnotation,
  GeometryPayload,
  GeometryShapePart,
} from '../types/mathGeometry';
import { geometryLayout, resolveGeometryCanvasSize, worldToSvg } from './geometryLayout';
import {
  getRightAngleMarkerPath,
  getTriangleAnnotationPoint,
  getTriangleLabelPositions,
  getTrianglePath,
  resolveTriangleSubtype,
} from './triangleGeometry';
import {
  regularPolygonSvgPoints,
  regularPolygonWorldBounds,
} from './regularPolygonGeometry';
import type { TriangleSubtype } from '../types/mathGeometry';

const HIGHLIGHT = '#c2410c';
const STROKE = 2;
const STROKE_HI = 2.5;
const LABEL_FONT_SIZE = 12;
const LABEL_FONT =
  "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, sans-serif";
const FILL_SHAPE = '#f8fafc';
const LABEL_BG = '#f8fafc';
const LABEL_BORDER = '#e2e8f0';
const INK = '#334155';

interface GeometryCanvasProps {
  geometry: GeometryPayload;
  width?: number;
  height?: number;
  printMode?: boolean;
  className?: string;
  caption?: string;
}

function edgeStroke(edge: GeometryEdge, highlight?: GeometryEdge[]) {
  return highlight?.includes(edge) ? STROKE_HI : STROKE;
}

function edgeColor(edge: GeometryEdge, highlight?: GeometryEdge[]) {
  return highlight?.includes(edge) ? HIGHLIGHT : 'currentColor';
}

function estimateTextWidth(text: string, fontSize = LABEL_FONT_SIZE) {
  return Math.max(fontSize * 0.55, text.length * fontSize * 0.58);
}

function DimensionLabel({
  x,
  y,
  text,
  anchor = 'middle',
  center = false,
}: {
  x: number;
  y: number;
  text: string;
  anchor?: 'start' | 'middle' | 'end';
  center?: boolean;
}) {
  if (!text) return null;

  if (center) {
    const textW = estimateTextWidth(text);
    const padX = 6;
    const padY = 4;
    const boxW = textW + padX * 2;
    const boxH = LABEL_FONT_SIZE + padY * 2;
    return (
      <g>
        <rect
          x={x - boxW / 2}
          y={y - boxH / 2}
          width={boxW}
          height={boxH}
          rx={4}
          fill={LABEL_BG}
          stroke={LABEL_BORDER}
          strokeWidth={1}
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={INK}
          fontSize={LABEL_FONT_SIZE}
          fontFamily={LABEL_FONT}
          fontWeight={500}
        >
          {text}
        </text>
      </g>
    );
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      dominantBaseline="middle"
      fill={INK}
      fontSize={LABEL_FONT_SIZE}
      fontFamily={LABEL_FONT}
      fontWeight={500}
    >
      {text}
    </text>
  );
}

function normalizeAnnotationAnchor(raw?: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

function AnnotationMarker({
  x,
  y,
  label,
  kind = 'vertex',
}: {
  x: number;
  y: number;
  label: string;
  kind?: 'vertex' | 'angle';
}) {
  if (!label) return null;

  if (kind === 'angle') {
    const textW = estimateTextWidth(label, 11);
    const padX = 5;
    const padY = 3;
    const boxW = textW + padX * 2;
    const boxH = 11 + padY * 2;
    return (
      <g>
        <rect
          x={x - boxW / 2}
          y={y - boxH / 2}
          width={boxW}
          height={boxH}
          rx={3}
          fill={LABEL_BG}
          stroke={LABEL_BORDER}
          strokeWidth={1}
          opacity={0.95}
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={INK}
          fontSize={11}
          fontFamily={LABEL_FONT}
          fontWeight={600}
        >
          {label}
        </text>
      </g>
    );
  }

  return (
    <g>
      <circle cx={x} cy={y} r={11} fill={LABEL_BG} stroke={LABEL_BORDER} strokeWidth={1.25} />
      <text
        x={x}
        y={y + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={INK}
        fontSize={12}
        fontFamily={LABEL_FONT}
        fontWeight={700}
      >
        {label}
      </text>
    </g>
  );
}

function AnnotationsLayer({
  annotations,
  shapeType,
  x,
  y,
  w,
  h,
  triangleSubtype,
}: {
  annotations?: GeometryAnnotation[];
  shapeType: 'rectangle' | 'triangle' | 'circle';
  x: number;
  y: number;
  w: number;
  h: number;
  triangleSubtype?: TriangleSubtype;
}) {
  if (!annotations?.length) return null;

  return (
    <g className="geometry-canvas__annotations">
      {annotations.map((annotation, index) => {
        const at = normalizeAnnotationAnchor(annotation.at);
        const kind =
          annotation.kind ||
          (at.includes('angle') || /°|alpha|β|γ/.test(annotation.label) ? 'angle' : 'vertex');
        const point =
          shapeType === 'triangle'
            ? getTriangleAnnotationPoint(triangleSubtype, x, y, w, h, at || `v${index}`)
            : shapeType === 'rectangle'
              ? rectangleAnnotationPoint(x, y, w, h, at || ['tl', 'tr', 'br', 'bl'][index] || 'tl')
              : { x: x + w / 2, y: y + h / 2, kind: 'vertex' as const };

        return (
          <AnnotationMarker
            key={`annotation-${index}`}
            x={point.x}
            y={point.y}
            label={annotation.label}
            kind={kind === 'angle' ? 'angle' : point.kind}
          />
        );
      })}
    </g>
  );
}

function rectangleAnnotationPoint(
  x: number,
  y: number,
  w: number,
  h: number,
  at: string,
): { x: number; y: number; kind: 'vertex' | 'angle' } {
  const anchor = normalizeAnnotationAnchor(at);
  if (anchor === 'tr' || anchor === 'topright') return { x: x + w, y, kind: 'vertex' };
  if (anchor === 'bl' || anchor === 'bottomleft') return { x, y: y + h, kind: 'vertex' };
  if (anchor === 'br' || anchor === 'bottomright') return { x: x + w, y: y + h, kind: 'vertex' };
  if (anchor === 'center') return { x: x + w / 2, y: y + h / 2, kind: 'vertex' };
  if (anchor.includes('angle')) return { x: x + 16, y: y + 16, kind: 'angle' };
  return { x, y, kind: 'vertex' };
}

function RectangleShape({
  x,
  y,
  w,
  h,
  labels,
  highlightEdges,
  annotations,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  labels?: GeometryLabels;
  highlightEdges?: GeometryEdge[];
  annotations?: GeometryAnnotation[];
}) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={FILL_SHAPE}
        stroke="currentColor"
        strokeWidth={STROKE}
        vectorEffect="non-scaling-stroke"
      />
      {highlightEdges?.map((edge) => {
        if (edge === 'top') {
          return (
            <line
              key={edge}
              x1={x}
              y1={y}
              x2={x + w}
              y2={y}
              stroke={edgeColor(edge, highlightEdges)}
              strokeWidth={edgeStroke(edge, highlightEdges)}
            />
          );
        }
        if (edge === 'bottom') {
          return (
            <line
              key={edge}
              x1={x}
              y1={y + h}
              x2={x + w}
              y2={y + h}
              stroke={edgeColor(edge, highlightEdges)}
              strokeWidth={edgeStroke(edge, highlightEdges)}
            />
          );
        }
        if (edge === 'left') {
          return (
            <line
              key={edge}
              x1={x}
              y1={y}
              x2={x}
              y2={y + h}
              stroke={edgeColor(edge, highlightEdges)}
              strokeWidth={edgeStroke(edge, highlightEdges)}
            />
          );
        }
        if (edge === 'right') {
          return (
            <line
              key={edge}
              x1={x + w}
              y1={y}
              x2={x + w}
              y2={y + h}
              stroke={edgeColor(edge, highlightEdges)}
              strokeWidth={edgeStroke(edge, highlightEdges)}
            />
          );
        }
        return null;
      })}
      <DimensionLabel x={cx} y={y - 10} text={labels?.top ?? ''} />
      <DimensionLabel x={x + w + 14} y={cy} text={labels?.right ?? ''} anchor="start" />
      <DimensionLabel x={cx} y={y + h + 14} text={labels?.bottom ?? ''} />
      <DimensionLabel x={x - 10} y={cy} text={labels?.left ?? ''} anchor="end" />
      {labels?.center && (
        <DimensionLabel x={cx} y={cy} text={labels.center} center />
      )}
      <AnnotationsLayer
        annotations={annotations}
        shapeType="rectangle"
        x={x}
        y={y}
        w={w}
        h={h}
      />
    </g>
  );
}

function TriangleShape({
  x,
  y,
  w,
  h,
  labels,
  annotations,
  triangleSubtype,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  labels?: GeometryLabels;
  annotations?: GeometryAnnotation[];
  triangleSubtype?: TriangleSubtype;
}) {
  const subtype = resolveTriangleSubtype(triangleSubtype, labels, annotations);
  const path = getTrianglePath(subtype, x, y, w, h);
  const rightAnglePath = getRightAngleMarkerPath(subtype, x, y, w, h);
  const labelPos = getTriangleLabelPositions(subtype, x, y, w, h);

  return (
    <g>
      <path
        d={path}
        fill={FILL_SHAPE}
        stroke="currentColor"
        strokeWidth={STROKE}
        vectorEffect="non-scaling-stroke"
      />
      {rightAnglePath ? (
        <path
          d={rightAnglePath}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          opacity={0.85}
        />
      ) : null}
      <DimensionLabel x={labelPos.bottom.x} y={labelPos.bottom.y} text={labels?.bottom ?? labels?.top ?? ''} />
      <DimensionLabel x={labelPos.left.x} y={labelPos.left.y} text={labels?.left ?? ''} anchor="end" />
      <DimensionLabel x={labelPos.right.x} y={labelPos.right.y} text={labels?.right ?? ''} anchor="start" />
      {labels?.center ? (
        <DimensionLabel x={labelPos.center.x} y={labelPos.center.y} text={labels.center} center />
      ) : null}
      <AnnotationsLayer
        annotations={annotations}
        shapeType="triangle"
        x={x}
        y={y}
        w={w}
        h={h}
        triangleSubtype={subtype}
      />
    </g>
  );
}

function CircleShape({
  cx,
  cy,
  r,
  labels,
}: {
  cx: number;
  cy: number;
  r: number;
  labels?: GeometryLabels;
}) {
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={FILL_SHAPE}
        stroke="currentColor"
        strokeWidth={STROKE}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={cx}
        y1={cy}
        x2={cx + r}
        y2={cy}
        stroke="currentColor"
        strokeWidth={1.25}
        strokeDasharray="3 2"
        opacity={0.65}
      />
      <DimensionLabel x={cx + r / 2} y={cy - 8} text={labels?.right ?? labels?.top ?? ''} />
      {labels?.center && <DimensionLabel x={cx} y={cy + 4} text={labels.center} center />}
    </g>
  );
}

function RegularPolygonShape({
  cx,
  cy,
  radius,
  sides,
  labels,
}: {
  cx: number;
  cy: number;
  radius: number;
  sides: number;
  labels?: GeometryLabels;
}) {
  const points = regularPolygonSvgPoints(cx, cy, radius, sides);
  return (
    <g>
      <polygon
        points={points}
        fill={FILL_SHAPE}
        stroke="currentColor"
        strokeWidth={STROKE}
        vectorEffect="non-scaling-stroke"
      />
      <DimensionLabel x={cx} y={cy - radius - 10} text={labels?.top ?? ''} />
      {labels?.center ? <DimensionLabel x={cx} y={cy + 4} text={labels.center} center /> : null}
    </g>
  );
}

function renderPart(
  part: GeometryShapePart,
  layout: ReturnType<typeof geometryLayout>,
) {
  const ox = part.offsetX ?? 0;
  const oy = part.offsetY ?? 0;
  const d = part.dimensions;
  const s = layout.scale;

  if (part.shapeType === 'circle') {
    const r = (d.radius ?? 1) * s;
    const c = worldToSvg(ox, oy, layout);
    return (
      <CircleShape
        key={`${ox}-${oy}-c`}
        cx={c.x}
        cy={c.y}
        r={r}
        labels={part.labels}
      />
    );
  }

  if (part.shapeType === 'triangle') {
    const w = (d.base ?? d.width ?? 1) * s;
    const h = (d.height ?? 1) * s;
    const p = worldToSvg(ox, oy, layout);
    return (
      <TriangleShape
        key={`${ox}-${oy}-t`}
        x={p.x}
        y={p.y}
        w={w}
        h={h}
        labels={part.labels}
        annotations={part.annotations}
        triangleSubtype={part.triangleSubtype}
      />
    );
  }

  const w = (d.width ?? 1) * s;
  const h = (d.height ?? 1) * s;
  const p = worldToSvg(ox, oy, layout);
  return (
    <RectangleShape
      key={`${ox}-${oy}-r`}
      x={p.x}
      y={p.y}
      w={w}
      h={h}
      labels={part.labels}
      highlightEdges={part.highlightEdges}
    />
  );
}

function renderRoot(geometry: GeometryPayload, layout: ReturnType<typeof geometryLayout>) {
  const d = geometry.dimensions;
  const s = layout.scale;

  if (geometry.shapeType === 'circle') {
    const r = (d.radius ?? 1) * s;
    const c = worldToSvg(0, 0, layout);
    return <CircleShape cx={c.x} cy={c.y} r={r} labels={geometry.labels} />;
  }

  if (geometry.shapeType === 'triangle') {
    const w = (d.base ?? d.width ?? 1) * s;
    const h = (d.height ?? 1) * s;
    const p = worldToSvg(0, 0, layout);
    return (
      <TriangleShape
        x={p.x}
        y={p.y}
        w={w}
        h={h}
        labels={geometry.labels}
        annotations={geometry.annotations}
        triangleSubtype={geometry.triangleSubtype}
      />
    );
  }

  if (geometry.shapeType === 'regular_polygon') {
    const poly = regularPolygonWorldBounds(d);
    const radius = poly.radius * s;
    const c = worldToSvg(0, 0, layout);
    return (
      <RegularPolygonShape
        cx={c.x}
        cy={c.y}
        radius={radius}
        sides={poly.sides}
        labels={geometry.labels}
      />
    );
  }

  const w = (d.width ?? 1) * s;
  const h = (d.height ?? 1) * s;
  const p = worldToSvg(0, 0, layout);
  return (
    <RectangleShape
      x={p.x}
      y={p.y}
      w={w}
      h={h}
      labels={geometry.labels}
      highlightEdges={geometry.highlightEdges}
      annotations={geometry.annotations}
    />
  );
}

/** Plano geométrico SVG — proporción real, tipografía sans y estilo Serenity. */
export function GeometryCanvas({
  geometry,
  width,
  height,
  printMode = false,
  className = '',
  caption,
}: GeometryCanvasProps) {
  const canvasSize = useMemo(() => {
    if (width != null && height != null) {
      return { canvasW: width, canvasH: height };
    }
    return resolveGeometryCanvasSize(geometry);
  }, [geometry, width, height]);

  const { canvasW, canvasH } = canvasSize;

  const layout = useMemo(
    () => geometryLayout(geometry, canvasW, canvasH),
    [geometry, canvasW, canvasH],
  );

  const rootClass = [
    'geometry-canvas',
    printMode ? 'geometry-canvas--print' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const shapes =
    geometry.shapeType === 'compound' && geometry.parts?.length
      ? geometry.parts.map((part) => renderPart(part, layout))
      : renderRoot(geometry, layout);

  return (
    <figure className={rootClass}>
      {caption && <figcaption className="geometry-canvas__caption">{caption}</figcaption>}
      <svg
        className="geometry-canvas__svg"
        width="100%"
        height="auto"
        viewBox={`0 0 ${canvasW} ${canvasH}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={caption || 'Figura geométrica'}
      >
        <rect width="100%" height="100%" fill="#ffffff" />
        {shapes}
      </svg>
    </figure>
  );
}
